/*
 * Purpose: Workspace dropdown — switch workspace, prefs, support, sign out
 * Last modified: 2026-07-10 | Agent
 * Completeness: 95
 */
import { useNavigate } from 'react-router-dom';
import { BookOpen, Building2, ChevronDown, LogIn, LogOut, Moon, Shield, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useTheme, useResolvedTheme } from '@/components/theme-provider';
import { useAuthStore } from '@/stores/authStore';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { canAccessAdmin } from '@/lib/adminAccess';
import { APP_ROUTES } from '@/lib/appRoutes';

function WorkspaceMenuPreferences() {
  const { theme, setTheme } = useTheme();
  const resolved = useResolvedTheme();
  const nightOn = resolved === 'dark';

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs text-muted-foreground">Preferences</DropdownMenuLabel>
      <DropdownMenuItem
        className="flex items-center justify-between gap-3 focus:bg-accent"
        onSelect={(event) => event.preventDefault()}
      >
        <span className="flex items-center gap-2">
          <Moon className="h-4 w-4" />
          Night mode
        </span>
        <Switch
          checked={nightOn}
          onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
          aria-label="Toggle night mode"
        />
      </DropdownMenuItem>
      <DropdownMenuCheckboxItem
        checked={theme === 'system'}
        onCheckedChange={(checked) => setTheme(checked ? 'system' : resolved)}
        onSelect={(event) => event.preventDefault()}
      >
        Match system appearance
      </DropdownMenuCheckboxItem>
    </>
  );
}

export function AuthAccountMenu() {
  const navigate = useNavigate();
  const authed = useAuthStore((s) => s.authed);
  const email = useAuthStore((s) => s.email);
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const tenants = useAuthStore((s) => s.tenants);
  const selectTenant = useAuthStore((s) => s.selectTenant);
  const signOut = useAuthStore((s) => s.signOut);
  const tenantPickRequired = useAuthStore((s) => s.tenantPickRequired);
  const showAdmin = canAccessAdmin({ email, activeTenant });

  if (!authed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5 border-[var(--auth-accent)]/30 text-[var(--auth-fg)]"
          >
            <LogIn className="w-4 h-4" />
            <span className="hidden sm:inline">Account</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem
            onClick={() => navigate(`${AUTH_CONFIG.signInPath}?next=${encodeURIComponent('/')}`)}
          >
            <LogIn className="mr-2 h-4 w-4" />
            Sign in
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(APP_ROUTES.help)}>
            <BookOpen className="mr-2 h-4 w-4" />
            Support
          </DropdownMenuItem>
          <WorkspaceMenuPreferences />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (tenantPickRequired) {
    return (
      <Button
        size="sm"
        variant="default"
        className="shrink-0"
        onClick={() => navigate(`${AUTH_CONFIG.signInPath}?next=${encodeURIComponent('/')}`)}
      >
        <Building2 className="w-4 h-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Choose workspace</span>
      </Button>
    );
  }

  const initials = (email || 'U').slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 max-w-[12rem]">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--auth-accent)]/15 text-[10px] font-semibold text-[var(--auth-accent)]">
            {initials}
          </span>
          <span className="hidden sm:flex flex-col items-start min-w-0 leading-tight">
            <span className="text-xs font-medium truncate max-w-[8rem]">
              {activeTenant?.name ?? 'Workspace'}
            </span>
            <span className="text-[10px] text-muted-foreground truncate max-w-[8rem]">{email}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">Signed in as</span>
            <span className="text-sm truncate">{email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.length > 1 ? (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
            {tenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.id}
                onClick={() => void selectTenant(tenant.id)}
                className={activeTenant?.id === tenant.id ? 'bg-accent' : undefined}
              >
                <Building2 className="mr-2 h-4 w-4" />
                <span className="truncate">{tenant.name}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem onClick={() => navigate('/dashboard')}>
          <User className="mr-2 h-4 w-4" />
          Dashboard
        </DropdownMenuItem>
        {showAdmin ? (
          <DropdownMenuItem onClick={() => navigate(APP_ROUTES.admin)}>
            <Shield className="mr-2 h-4 w-4" />
            Administrator
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={() => navigate(APP_ROUTES.help)}>
          <BookOpen className="mr-2 h-4 w-4" />
          Support
        </DropdownMenuItem>
        <WorkspaceMenuPreferences />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void signOut();
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AuthAccountMenu;
